import {
  MUI_FIELD_NOTCH_CLOSED,
  MUI_FIELD_NOTCH_LEGEND,
  MUI_FIELD_NOTCH_OPEN,
  MUI_FIELD_OUTLINE,
} from "@/components/ui/mui-field-shape";
import { RequiredAsterisk } from "@/components/ui/required-asterisk";
import { cn } from "@/lib/utils";

// **An outlined field's line, and the hole the label sits in.** MUI's
// `NotchedOutline`, ported as itself (#846).
//
// MUI's notch is not paint. `OutlinedInput` draws its outline as a `<fieldset>`
// and puts a `<legend>` in the top border; the legend is `visibility: hidden`
// around an `opacity: 0` span, so it contributes no ink and does nothing but
// take up room — and the browser leaves the border out wherever it sits
// (`NotchedOutline.js:16-97`). The legend is sized by its own content, a copy
// of the label text, so **its width is the hole's width**, and MUI opens the
// hole by transitioning that width from nearly nothing to the whole label:
// `max-width: 0.01px` → `100%`, 100ms after a 50ms delay. The border tears open
// from the left while the label rises into it on its own longer 200ms clock.
//
// **Why the line moved in here.** Until #846 the line was the surface's own —
// `OutlinedField`'s inset shadow, a trigger's border — and this component was
// only a white band painted over it. A cover cannot match a cut. Wherever the
// field's top edge lands on a half pixel (the Edit Link dialog, over a page
// scrolled to 2393.5), the band and the line it covers are antialiased onto
// the same device row, and compositing one over the other leaves `c·(1−c)` of
// the ink: 240 where the donor's gap is 255, 203 under a focused line. Clearing
// that row would have meant painting above the field, which #561's D2 rules
// out. A real cut leaves nothing to clear, at any offset.
//
// Three more things come with the real fieldset rather than being computed:
// - **The focused line sits half a pixel high.** The border's top edge is
//   centred on the 11px legend, `(11 − width) / 2` below the fieldset's
//   `-5px` — `0` for 1px, `−0.5px` for MUI's focused 2px. At a fractional y
//   that decides which two rows the focused line covers.
// - **The gap starts at the content-box left** — the border plus `padding: 0
//   8px`, so 9px over a 1px line and 10px over 2px, with nothing animating
//   when focus moves it (the fieldset has no `border-width` transition).
// - **The gap's ends are snapped by the browser's own cut.** The 79.42px "Link
//   Name *" legend cuts to the nearest pixel, where the band's
//   `round(up, …)` width (#706) ran a pixel long.
//
// **Every outlined surface composes this one element** — the pin that says so,
// surface by surface, is `field-notch.layout.browser.test.tsx`, "every outlined
// surface composes the shared notch" (plus the Link URL box, pinned in
// `rich-text-field.browser.test.tsx` beside the flow that opens it). Each
// surface keeps its own state rules and hands this the colour and width
// through `--mui-outline-color` and `--mui-outline-width`
// (`MUI_FIELD_OUTLINE`); the control underneath keeps a transparent border of
// the same width, so nothing it lays out moves.

export interface FieldNotchProps {
  /**
   * The label text, without the asterisk — pass `required` for that.
   *
   * Callers hand over the bare string exactly as the donor does
   * (`InputField.tsx:50` passes `<OutlinedInput label={label}>`); it is
   * `OutlinedInput` itself that decides what the legend finally holds.
   */
  label: string;
  /**
   * Whether the field is required — which **widens the hole**.
   *
   * It is tempting to read `InputField.tsx:50` and conclude the gap is cut for
   * the bare label while the asterisk overhangs onto intact border. It is not:
   * on the way to `NotchedOutline`, `OutlinedInput` re-injects the two
   * characters when the `FormControl` is required —
   *
   * ```js
   * label: label != null && label !== '' && fcs.required
   *   ? <>{[label, "\u2009", '*']}</> : label   // OutlinedInput.js:224-228
   * ```
   *
   * — so the legend holds character-for-character what `FormLabel` renders
   * above it (`FormLabel.js:124-128`), and the border is absent under the
   * asterisk with the legend's usual 5px of clearance. Every required donor
   * call site reaches this: `RequestItemIsland.tsx:100` (Item Name) through
   * `InputField`'s `...rest`, and `CustomSelectWithPagination.tsx:195`
   * (`required={required}` on the `FormControl`) for Manufacturer, Finish and
   * Material.
   */
  required?: boolean;
  /**
   * Whether the hole is open — the same flag the label shrinks on.
   *
   * Not two decisions: `OutlinedInput`'s `notched` defaults to
   * `Boolean(startAdornment || filled || focused)` and `InputLabel`'s `shrink`
   * is `filled || focused || adornedStart`, which is the same condition
   * spelled twice.
   */
  notched: boolean;
  /**
   * **The line's state** — the colour and width rules a surface paints its
   * outline with, merged onto the fieldset.
   *
   * A surface whose state lives on an ancestor (`OutlinedField`'s frame) sets
   * the two custom properties there and passes nothing. A popover-backed
   * control is this element's *previous sibling*, so it passes
   * `MUI_FIELD_OUTLINE_PEER`, which reads the control's `:focus`, open menu,
   * `aria-invalid` and `:disabled` through Tailwind's `peer-*`.
   */
  className?: string;
  /**
   * **The pickers' legend is tracked.** `PickersInputBase` gives its root
   * `0.00938em` of letter-spacing (`PickersInputBase.mjs:48`) and the legend
   * inherits the computed length, 0.15008px at the root's 16px, where
   * `OutlinedInput`'s legend inherits `normal`. Only the date field sets it.
   */
  pickersTracking?: boolean;
}

export function FieldNotch({
  label,
  notched,
  required,
  className,
  pickersTracking = false,
}: FieldNotchProps) {
  return (
    // Decorative, and the label beside it already carries this text — so to a
    // screen reader it is MUI's `aria-hidden` fieldset, not a second copy of
    // the field's name (`NotchedOutline.js:112`).
    <fieldset
      aria-hidden="true"
      data-slot="field-outline"
      className={cn(MUI_FIELD_OUTLINE, className)}
    >
      <legend
        data-slot="field-notch"
        // The state marker every ported field stamps, so "is the hole open"
        // reads the same way as `data-shrink` on the label it belongs to.
        data-notched={notched}
        className={cn(
          MUI_FIELD_NOTCH_LEGEND,
          pickersTracking && "tracking-[0.15008px]",
          notched ? MUI_FIELD_NOTCH_OPEN : MUI_FIELD_NOTCH_CLOSED,
        )}
      >
        {/* MUI's `& > span`: `display: inline-block` with `padding: 0 5px`,
            `opacity: 0` and `visibility: visible` (`NotchedOutline.js:76-83`).
            The padding belongs in here and not on the legend for a reason that
            only shows at the closed end — under `box-sizing: border-box` a
            `max-width` cannot shrink a box below its own padding, so 5px
            either side would floor the closed notch at 10px and leave a
            permanent hole in the border where the donor has none. */}
        <span className="visible inline-block px-[5px] opacity-0">
          {label}
          {/* The same component the label uses, so U+2009 and '*' keep the one
              home `required-asterisk.tsx` was split out to give them — and the
              hole stays exactly as wide as the glyphs it makes room for. */}
          {required ? <RequiredAsterisk /> : null}
        </span>
      </legend>
    </fieldset>
  );
}
